import { useCallback, useRef, useState } from 'react';

const MAX_HISTORY_SIZE = 50;

type UndoRedoResult<T> = {
  undo: () => T | undefined;
  redo: () => T | undefined;
  canUndo: boolean;
  canRedo: boolean;
  recordSnapshot: (snapshot: T) => void;
  replaceHistory: (snapshot: T) => void;
};

export const useUndoRedo = <T extends Record<string, unknown>>(initialSnapshot: T): UndoRedoResult<T> => {
  const [history, setHistory] = useState<T[]>([initialSnapshot]);
  const [currentIndex, setCurrentIndex] = useState(0);
  const currentIndexRef = useRef(0);

  const recordSnapshot = useCallback((snapshot: T) => {
    setHistory((prevHistory) => {
      const newHistory = prevHistory.slice(0, currentIndexRef.current + 1);
      newHistory.push(snapshot);

      if (newHistory.length > MAX_HISTORY_SIZE) {
        return newHistory.slice(newHistory.length - MAX_HISTORY_SIZE);
      }

      return newHistory;
    });

    setCurrentIndex((prevIndex) => {
      const newIndex = prevIndex + 1;
      const finalIndex = newIndex >= MAX_HISTORY_SIZE ? MAX_HISTORY_SIZE - 1 : newIndex;
      currentIndexRef.current = finalIndex;
      return finalIndex;
    });
  }, []);

  const undo = useCallback((): T | undefined => {
    if (currentIndexRef.current > 0) {
      const newIndex = currentIndexRef.current - 1;
      currentIndexRef.current = newIndex;
      setCurrentIndex(newIndex);
      return history[newIndex];
    }
    return undefined;
  }, [history]);

  const redo = useCallback((): T | undefined => {
    if (currentIndexRef.current < history.length - 1) {
      const newIndex = currentIndexRef.current + 1;
      currentIndexRef.current = newIndex;
      setCurrentIndex(newIndex);
      return history[newIndex];
    }
    return undefined;
  }, [history]);

  const replaceHistory = useCallback((snapshot: T) => {
    currentIndexRef.current = 0;
    setHistory([snapshot]);
    setCurrentIndex(0);
  }, []);

  const canUndo = currentIndex > 0;
  const canRedo = currentIndex < history.length - 1;

  return {
    undo,
    redo,
    canUndo,
    canRedo,
    recordSnapshot,
    replaceHistory,
  };
};
